import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { EMPTY, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { AuthenticationCookieService } from '@authentication/authentication-cookie.service';
import { AuthenticationService } from '@authentication/authentication.service';
import { ApiHttpErrorResponse } from '@classes/api-http-error-response';
import { MessageService } from '@shared/services/message.service';
import { TubErrorReportingService } from '@shared/services/tub-error-reporting.service';
import { ConnectionService } from '@shared/services/connection.service';

function getAuthenticatedRequest(
  req: HttpRequest<unknown>,
  authenticationCookieService: AuthenticationCookieService
): HttpRequest<unknown> {
  const sessionCookieToken = authenticationCookieService.getSessionCookie();
  if (sessionCookieToken) {
    return req.clone({
      setHeaders: {
        Authorization: 'Bearer ' + sessionCookieToken,
      },
    });
  }
  return req;
}

/**
 * Functional interceptor responsible for inserting the Authorization Bearer token into all requests bound for TUB
 */
export const apiInterceptorFn: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authenticationCookieService = inject(AuthenticationCookieService);
  const authenticationService = inject(AuthenticationService);
  const messageService = inject(MessageService);
  const tubErrorReportingService = inject(TubErrorReportingService);
  const connectionService = inject(ConnectionService);

  // Swallow all errors when the user has lost connection
  if (!connectionService.isOnline()) {
    return EMPTY;
  }

  // Only add auth headers for requests to the thrive unified backend
  if (!req.url.startsWith(environment.thriveUnifiedBackend.rootUrl)) {
    return next(req);
  }

  // Populate the request with the session cookie
  req = getAuthenticatedRequest(req, authenticationCookieService);

  return next(req).pipe(
    catchError((err: ApiHttpErrorResponse) => {
      if (!(err instanceof HttpErrorResponse)) {
        return throwError(() => err);
      }

      // Extract the first line of the error response to distinguish between different flavours of errors
      if (typeof err.error === 'string') {
        err.tubErrorToken = err.error.split('\n').map((error) => error.trim())[0];
      }

      switch (err.status) {
        case 400:
          return throwError(() => err);
        case 401:
          if (err.tubErrorToken !== 'MfaRequiredError') {
            return authenticationService.openAuthenticationDialog().pipe(
              switchMap(() => {
                const retryReq = getAuthenticatedRequest(req, authenticationCookieService);
                return next(retryReq);
              })
            );
          }
          return throwError(() => err);
        case 404:
          messageService.showMessage('Error: Resource not found');
          return throwError(() => err);
        case 409: // ConflictError (should be communicated to the user in context)
        case 403: // ForbiddenError (should be communicated to the user in context)
          return throwError(() => err);
        default:
          tubErrorReportingService.send('Unexpected error code received from tub', err, { req });
          messageService.showMessage(
            'Error: An unknown error has occurred.  If the problem persists, then please contact support'
          );
          return throwError(() => err);
      }
    })
  );
};
